// C29 — lightweight onboarding coach-marks: a spotlight over key UI with Next / Back / Skip. Targets are matched
// by selector and degrade gracefully (a step whose element isn't on screen is simply centred). Replayable from the
// "Guide" button; auto-opens once for a new user (localStorage flag).
import { useEffect, useLayoutEffect, useState } from 'react';
import { useDis } from './store';

export const TOUR_SEEN_KEY = 'disos.tourSeen';

interface Step { targets?: string[]; title: string; body: string }
const STEPS: Step[] = [
  { title: 'Welcome to disOS', body: 'A quick tour of the workspace — about a minute. You can skip anytime.' },
  { targets: ['#grid'], title: 'Enter the case', body: "Type each parent's income and deductions in the grid. Tab moves between cells; on a money cell press Space to choose a pay period (bi-weekly, semi-monthly…). Drag the column edges to resize." },
  { targets: ['[data-tour="results"]'], title: 'Live results', body: 'Support recalculates as you type — the guideline amount and your proposed scenario, side by side.' },
  { targets: ['[data-tour="summary"]', '[data-tour="results"]'], title: 'Summary', body: 'The Summary portion distils net spendable income, the add-on split and the bonus range. Drag its edge to resize, or close it with ✕ (reopen with “+ Summary”).' },
  { targets: ['[data-tour="tab-reports"]'], title: 'Court-ready reports', body: 'Open Reports to preview and print bench reports, findings, the client summary and more.' },
  { title: "You're set", body: 'Everything autosaves as you work. Replay this tour anytime from “Guide” in the top bar.' },
];

const PAD = 6;

export function Tour() {
  const open = useDis((s) => s.ui.tourOpen);
  const setUi = useDis((s) => s.setUi);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const close = () => { setUi({ tourOpen: false }); try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch { /* ignore */ } };

  // Reset to the first step whenever the tour (re)opens.
  useEffect(() => { if (open) setI(0); }, [open]);

  const step = STEPS[i];
  // Measure the current step's target (first selector that resolves) on each step change and on resize/scroll.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const sel = step.targets?.map((s) => document.querySelector(s)).find(Boolean) as HTMLElement | undefined;
      setRect(sel ? sel.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [open, i, step]);

  if (!open) return null;

  const last = i === STEPS.length - 1;
  // Card position: below the target if there's room, else above; centred when there's no target.
  const vw = window.innerWidth, vh = window.innerHeight, CARD = 320;
  let cardStyle: React.CSSProperties;
  if (rect) {
    const below = rect.bottom + 150 < vh;
    const left = Math.min(Math.max(12, rect.left + rect.width / 2 - CARD / 2), vw - CARD - 12);
    cardStyle = { left, top: below ? rect.bottom + PAD + 8 : undefined, bottom: below ? undefined : vh - rect.top + PAD + 8 };
  } else {
    cardStyle = { left: vw / 2 - CARD / 2, top: vh / 2 - 80 };
  }

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label="disOS tour" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      {rect
        ? <div className="tour-spot" style={{ left: rect.left - PAD, top: rect.top - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }} />
        : <div className="tour-dim" />}
      <div className="tour-card" style={cardStyle}>
        <div className="tour-step">Step {i + 1} of {STEPS.length}</div>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
        <div className="tour-acts">
          <button type="button" className="tour-skip" onClick={close}>{last ? 'Done' : 'Skip'}</button>
          <span className="sp" />
          {i > 0 && <button type="button" className="tour-btn" onClick={() => setI((n) => n - 1)}>Back</button>}
          {last
            ? <button type="button" className="tour-btn pri" onClick={close}>Finish</button>
            : <button type="button" className="tour-btn pri" onClick={() => setI((n) => Math.min(STEPS.length - 1, n + 1))}>Next</button>}
        </div>
      </div>
    </div>
  );
}
